import { BackrsFormValues, BackrsUser, ProfileOptionAnswer } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth, baseQueryWithoutAuth } from '../baseQuery';

type AutocompleteResultItem = Record<string, string>;

type AutocompleteResponse = Readonly<{
  results: AutocompleteResultItem[];
}>;

// Define the return type for the autocomplete query
export type AutocompleteResult = Readonly<{
  endpoint: string;
  values: {
    label: string;
    value: string;
  }[];
  searchTerm?: string;
}>;

type ApiFieldError = Readonly<{
  value: string;
  msg: string;
  param: string;
  location: string;
}>;

type ApiErrorResponse = Readonly<{
  errors: ApiFieldError[];
  message: string;
}>;

export type FamilyMembership = Readonly<{
  familyAccountId: number;
  autoAccepted: boolean;
}>;

export type RegisterResponse = Readonly<{
  user: BackrsUser & {
    familyMembership: FamilyMembership | null;
  };
}>;

export type ProfileOptionAnswerBatchPostRequest = Readonly<Record<string, ProfileOptionAnswer[]>>;

/**
 * Public API for unauthenticated onboarding endpoints (pre-login)
 */
export const onboardingPublicApi = createApi({
  reducerPath: 'onboardingPublicApi',
  baseQuery: baseQueryWithoutAuth,
  endpoints: (builder) => ({
    // Registration - POST /users/register
    postUser: builder.mutation<
      RegisterResponse,
      { userData: BackrsFormValues; guestToken?: string }
    >({
      query: ({ userData, guestToken }) => ({
        url: '/users/register',
        method: 'POST',
        headers: guestToken ? { Authorization: `Bearer ${guestToken}` } : undefined,
        body: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          phone: userData.phone,
          email: userData.email,
          password: userData.password,
          is_fourteen_plus: true,
          register_id: null,
          invite_code: userData.invite_code,
          zip_code: userData.zip_code,
          grade: userData.grade,
          school: userData.school,
          onboarding_type: userData.onboarding_type,
          accepted_terms: true,
        },
      }),
      transformResponse: (response: RegisterResponse) => response,
      transformErrorResponse: (response) => {
        const data = response.data as ApiErrorResponse;
        const extractedErrors = data?.errors?.reduce(
          (acc: Record<string, string>, curr: ApiFieldError) => {
            if (curr.param && curr.msg) {
              acc[curr.param] = curr.msg;
            }

            return acc;
          },
          {},
        ) || { general: data?.message || 'Unknown error' };

        return {
          status: response.status,
          data: extractedErrors,
        };
      },
    }),

    // Validate email/phone uniqueness - POST /users/validate-registration
    validateRegistration: builder.mutation<{ valid: boolean }, { email?: string; phone?: string }>({
      query: ({ email, phone }) => ({
        url: '/users/validate-registration',
        method: 'POST',
        body: { email, phone },
      }),
      transformResponse: () => ({ valid: true }),
      transformErrorResponse: (response) => {
        const data = response.data as ApiErrorResponse;
        const extractedErrors = data?.errors?.reduce(
          (acc: Record<string, string>, curr: ApiFieldError) => {
            if (curr.param && curr.msg) {
              acc[curr.param] = curr.msg;
            }

            return acc;
          },
          {},
        ) || { general: 'Validation failed' };

        return {
          status: response.status,
          data: extractedErrors,
        };
      },
    }),
  }),
});

/**
 * Authenticated API for onboarding endpoints (post-login)
 */
export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    // Autocomplete - GET {endpoint}?q={searchTerm}
    fetchAutocompleteOptions: builder.query<
      AutocompleteResult[],
      { endpoint: string; searchTerm: string }
    >({
      queryFn: async ({ endpoint, searchTerm }, _queryApi, _extraOptions, baseQuery) => {
        // Full text search must be at least 4 characters
        if (!endpoint || !searchTerm || searchTerm.length < 4) {
          return { data: [] };
        }

        const result = await baseQuery(`${endpoint}?q=${encodeURIComponent(searchTerm)}`);

        if (result.error) {
          return { error: result.error };
        }

        const response = result.data as AutocompleteResponse;
        const values = response.results
          .map((item) => {
            for (const key in item) {
              if (item[key]) {
                return { label: item[key], value: item[key] };
              }
            }

            return { label: '', value: '' };
          })
          .filter(Boolean);

        return { data: [{ endpoint, values }] };
      },
    }),

    // PATCH /user-profile (new method)
    postProfileOption: builder.mutation<
      { success: boolean },
      {
        profileOptionAnswers: Record<string, string | number>;
        postKey: string;
      }
    >({
      query: ({ profileOptionAnswers }) => ({
        url: '/user-profile',
        method: 'PATCH',
        body: profileOptionAnswers,
      }),
      transformResponse: () => ({ success: true }),
    }),

    // PATCH /profile (old method via profile option answers)
    postProfileOptionAnswer: builder.mutation<
      { success: boolean },
      {
        profileOptionAnswers: ProfileOptionAnswer[];
        postKey: string;
      }
    >({
      query: ({ profileOptionAnswers, postKey }) => ({
        url: '/profile',
        method: 'PATCH',
        body: { [postKey]: profileOptionAnswers },
      }),
      transformResponse: () => ({ success: true }),
    }),

    // POST /users/invite-code - Process invite code for existing user
    processInviteCodeForExistingUser: builder.mutation<{ success: boolean }, { code: string }>({
      query: ({ code }) => ({
        url: '/users/invite-code',
        method: 'POST',
        body: { code },
      }),
      transformResponse: (response: { success?: boolean }): { success: boolean } => ({
        success: response.success ?? true,
      }),
    }),
  }),
});

// Export hooks from public API (unauthenticated)
export const { usePostUserMutation, useValidateRegistrationMutation } = onboardingPublicApi;

// Export hooks from authenticated API
export const {
  useLazyFetchAutocompleteOptionsQuery,
  usePostProfileOptionAnswerMutation,
  usePostProfileOptionMutation,
  useProcessInviteCodeForExistingUserMutation,
  util: { resetApiState },
} = onboardingApi;
